//@flow
import * as React from 'react';
import styled from 'styled-components';
import {useIsMobileAgent} from 'components/Responsive';
import Settings from 'config/Settings';
const LocalizedContext: Object = React.createContext(null);

export type LocalizedText = {
  key?: string,
  datatext: string,
  datafont: string,
  datasize: string | number,
  datalineheight: string | number,
  datacolor: string,
  dataletterspacing?: number,
  datatextshadowx?: number,
  datatextshadowy?: number,
  datatextshadowr?: number,
  datatextshadowc?: string,
  datatextalign?: string,
};

function parseSizes(
  value: string | number | typeof undefined,
  index: number,
  defaultValue?: number
): number | typeof undefined {
  if (typeof value === 'string') {
    const values = value.split(',');
    const numValue = values.length > index ? parseFloat(values[index]) : undefined;
    return isNaN(numValue) ? defaultValue : numValue;
  }
  return value ?? defaultValue;
}
function parseRGBA(value: string | typeof undefined): string {
  if (typeof value === 'string') {
    const values = value.split(',');
    if (values.length === 1) {
      return values[0];
    }
    if (values.length >= 2) {
      const rgb = hexToRgb(values[0]);
      if (rgb) {
        return `rgba(${rgb.r},${rgb.g},${rgb.b},${values[1]})`
      }
    }
  }
  return '';
}
function hexToRgb(hex) {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function(m, r, g, b) {
    return r + r + g + g + b + b;
  });

  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}
export default function Localized(props: any): React.Element<any> {
  const mobile = useIsMobileAgent();

  const localTexts = React.useMemo(() => Object.assign({}, ...Settings.localTexts.map(value => value)), []);
  const localizedTextMobile: Object = React.useMemo(
    () =>
      Object.assign(
        {},
        ...Object.keys(localTexts).map((k) => ({
          [k]: {
            datatext: localTexts[k].text,
            datafont: localTexts[k].font,
            datacolor: localTexts[k].color,
            datasize: parseSizes(localTexts[k].sizeMobile, 0),
            datalineheight: parseSizes(localTexts[k].sizeMobile, 1, parseSizes(localTexts[k].sizeMobile, 0)),
            dataletterspacing: parseSizes(localTexts[k].sizeMobile, 2, 0),
            datatextshadowx: parseSizes(localTexts[k].shadowSize, 0, 0),
            datatextshadowy: parseSizes(localTexts[k].shadowSize, 1, 0),
            datatextshadowr: parseSizes(localTexts[k].shadowSize, 2, 0),
            datatextshadowc: parseRGBA(localTexts[k].shadowColor)
          },
        }))
      ),
    [localTexts]
  );

  const localizedTextDesktop: Object = React.useMemo(
    () =>
      Object.assign(
        {},
        ...Object.keys(localTexts).map((k) => ({
          [k]: {
            datatext: localTexts[k].text,
            datafont: localTexts[k].font,
            datacolor: localTexts[k].color,
            datasize: parseSizes(localTexts[k].sizeDesktop, 0),
            datalineheight: parseSizes(localTexts[k].sizeDesktop, 1, parseSizes(localTexts[k].sizeDesktop, 0)),
            dataletterspacing: parseSizes(localTexts[k].sizeDesktop, 2, 0),
            datatextshadowx: parseSizes(localTexts[k].shadowSize, 0, 0),
            datatextshadowy: parseSizes(localTexts[k].shadowSize, 1, 0),
            datatextshadowr: parseSizes(localTexts[k].shadowSize, 2, 0),
            datatextshadowc: parseRGBA(localTexts[k].shadowColor)
          },
        }))
      ),
    [localTexts]
  );

  return (
    <LocalizedContext.Provider value={mobile ? localizedTextMobile : localizedTextDesktop}>
      {props.children}
    </LocalizedContext.Provider>
  );
}

export function useLocalized(): Object {
  const state = React.useContext(LocalizedContext);
  if (!state) {
    throw new Error('Cannot find Provider');
  }
  return state;
}

const StyledParagraph: React.AbstractComponent<any> = styled.p`
  color: ${(props) => props.datacolor};
  font-family: ${(props) => props.datafont};
  font-size: ${(props) => props.datasize}px;
  line-height: ${(props) => props.datalineheight}px;
  letter-spacing: ${(props) => props.dataletterspacing > 0 ? `${props.dataletterspacing}px` : undefined};
  text-shadow: ${(props) => props.datatextshadowr > 0 ? `${props.datatextshadowx}px ${props.datatextshadowy}px ${props.datatextshadowr}px ${props.datatextshadowc}` : undefined};
  text-align: ${props => props.datatextalign};
  white-space: pre-line;
`;

export type PropsLocalizedParagraph = LocalizedText & {
  children?: React.Node,
}

export function LocalizedParagraph(props: PropsLocalizedParagraph): React.Element<any> {
  const {datatext, ...others} = props;
  return <StyledParagraph {...others}>{datatext}{props.children}</StyledParagraph>;
}

const StyledSpan: React.AbstractComponent<any> = styled.span`
  color: ${(props) => props.datacolor};
  font-family: ${(props) => props.datafont};
  font-size: ${(props) => props.datasize}px;
  line-height: ${(props) => props.datalineheight}px;
  letter-spacing: ${(props) => props.dataletterspacing > 0 ? `${props.dataletterspacing}px` : undefined};
  text-shadow: ${(props) => props.datatextshadowr > 0 ? `${props.datatextshadowx}px ${props.datatextshadowy}px ${props.datatextshadowr}px ${props.datatextshadowc}` : undefined};
  white-space: pre-line;
`;

type PropsLocalizedParagraphFormat = {
  format: LocalizedText,
  args: Array<LocalizedText | string>,
};

export function LocalizedParagraphFormat(props: PropsLocalizedParagraphFormat): React.Element<any> {
  const {format, args, ...others} = props;
  const {datatext, ...styles} = props.format;
  console.assert(datatext);
  const input = React.useMemo(() => datatext?.split(/({\d})/) ?? [], [datatext]);
  const nodes = React.useMemo(() => {
    return input.map((value) => {
      if (value.indexOf('{') < 0) {
        return value;
      }
      const idx = parseInt(value.substring(1, value.length - 1));
      if (props.args.length > idx && idx >= 0) {
        if (typeof props.args[idx] === 'string') {
          return <span key={idx}>{props.args[idx]}</span>;
        }
        return (
          <StyledSpan key={idx} {...props.args[idx]}>
            {props.args[idx].datatext}
          </StyledSpan>
        );
      }
      return null;
    });
  }, [input, props.args]);

  return (
    <StyledParagraph
      {...others}
      datacolor={styles.datacolor}
      datafont={styles.datafont}
      datasize={styles.datasize}
      datalineheight={styles.datalineheight}
      dataletterspacing={styles.dataletterspacing}
      datatextshadowx={styles.datatextshadowx}
      datatextshadowy={styles.datatextshadowy}
      datatextshadowr={styles.datatextshadowr}
      datatextshadowc={styles.datatextshadowc}
      >
      {nodes}
    </StyledParagraph>
  );
}

type PropsParagraph = {
  textKey: string,
  textAlignCenter?: boolean,
};

export function Paragraph(props: PropsParagraph): React.Element<any> {
  const {textKey, ...others} = props;
  const localized = useLocalized();
  console.assert(localized[textKey], textKey);
  const {datatext, ...styles} = localized[textKey];
  console.assert(datatext, textKey);
  const input = React.useMemo(() => datatext?.split(/({\$+\w+})/) ?? [], [datatext]);
  const nodes = React.useMemo(() => {
    return input.map((value) => {
      if (value.indexOf('{') < 0) {
        return value;
      }
      const argKey = value.substring(1, value.length - 1);
      if (argKey.length > 0 && argKey[0] === '$') {
        const argLocalKey = argKey.substring(1);
        const argLocalText = localized[argLocalKey]
        if (argLocalText) {
          return <StyledSpan key={argLocalKey} {...argLocalText}>
            {argLocalText.datatext}
          </StyledSpan>;
        }
      }
      return value;
    });
  }, [input, localized]);

  return (
    <StyledParagraph
      {...others}
      datacolor={styles.datacolor}
      datafont={styles.datafont}
      datasize={styles.datasize}
      datalineheight={styles.datalineheight}
      dataletterspacing={styles.dataletterspacing}
      datatextshadowx={styles.datatextshadowx}
      datatextshadowy={styles.datatextshadowy}
      datatextshadowr={styles.datatextshadowr}
      datatextshadowc={styles.datatextshadowc}
      datatextalign={props.textAlignCenter ? 'center' : undefined}
      >
      {nodes}
    </StyledParagraph>
  );
}

const StyledLinkButton: React.AbstractComponent<any> = styled.a`
  display: inline-block;
  color: ${(props) => props.datacolor};
  font-family: ${(props) => props.datafont};
  font-size: ${(props) => props.datasize}px;
  line-height: ${(props) => props.datalineheight}px;
  letter-spacing: ${(props) => props.dataletterspacing > 0 ? `${props.dataletterspacing}px` : undefined};
  text-shadow: ${(props) => props.datatextshadowr > 0 ? `${props.datatextshadowx}px ${props.datatextshadowy}px ${props.datatextshadowr}px ${props.datatextshadowc}` : undefined};
  white-space: pre-line;
  text-decoration: none;
  vertical-align: middle;
  text-align:center;
  pointer-events: auto;
`;

const StyledButton: React.AbstractComponent<any> = styled.button`
  color: ${(props) => props.datacolor};
  font-family: ${(props) => props.datafont};
  font-size: ${(props) => props.datasize}px;
  line-height: ${(props) => props.datalineheight}px;
  letter-spacing: ${(props) => props.dataletterspacing > 0 ? `${props.dataletterspacing}px` : undefined};
  text-shadow: ${(props) => props.datatextshadowr > 0 ? `${props.datatextshadowx}px ${props.datatextshadowy}px ${props.datatextshadowr}px ${props.datatextshadowc}` : undefined};
  white-space: pre-line;
`;

export function LocalizedButton(props: LocalizedText): React.Element<any> {
  const {datatext, ...others} = props;
  return <StyledButton {...others}>{datatext}</StyledButton>;
}

export function LocalizedLinkButton(props: LocalizedText): React.Element<any> {
  const {datatext, ...others} = props;
  return <StyledLinkButton {...others}>{datatext}</StyledLinkButton>;
}

const StyledDiv: React.AbstractComponent<any> = styled.div`
  color: ${(props) => props.datacolor};
  font-family: ${(props) => props.datafont};
  font-size: ${(props) => props.datasize}px;
  line-height: ${(props) => props.datalineheight}px;
  letter-spacing: ${(props) => props.dataletterspacing > 0 ? `${props.dataletterspacing}px` : undefined};
  text-shadow: ${(props) => props.datatextshadowr > 0 ? `${props.datatextshadowx}px ${props.datatextshadowy}px ${props.datatextshadowr}px ${props.datatextshadowc}` : undefined};
  white-space: pre-line;
`;

export function LocalizedDiv(props: LocalizedText): React.Element<any> {
  const {datatext, ...others} = props;
  return <StyledDiv {...others}>{datatext}</StyledDiv>;
}

export {StyledParagraph, StyledButton, StyledLinkButton, StyledDiv};
